import * as React from 'react';
import { useEffect, useState } from 'react';
import { Heading } from '@restworld/ui-ds-base';
import { ArrowLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { animated, useTransition } from '@react-spring/web';
import { useMobileNavbarCrossingAnimation } from '@restworld/ui-components';
import { Button } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';

export type MobileTabProp = {
  label: string | React.ReactNode;
  title: string | React.ReactNode;
  content: string | React.ReactNode;
  badgeContent?: string | number | React.ReactNode;
};

export type MobileTabsProps = {
  tabs: MobileTabProp[];
  defaultTab?: number;
  onTabChange?: (index?: number) => void;
};

export const MobileTabs = ({ tabs, onTabChange, defaultTab }: MobileTabsProps) => {
  const [activeTab, setActiveTab] = useState<number | undefined>(undefined);
  const [overlayVisible, setOverlayVisible] = useState<boolean>(false);

  const { hasCrossedNavbar } = useMobileNavbarCrossingAnimation();

  useEffect(() => {
    if (overlayVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [overlayVisible]);

  useEffect(() => {
    if (defaultTab !== undefined) {
      setActiveTab(defaultTab);
      setOverlayVisible(true);
      onTabChange && onTabChange(defaultTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);

  const showOverlay = (tabIndex: number) => {
    setActiveTab(tabIndex);
    setOverlayVisible(true);
    onTabChange && onTabChange(tabIndex);
  };

  const hideOverlay = () => {
    setOverlayVisible(false);
    setActiveTab(undefined);
    onTabChange && onTabChange(undefined);
  };

  const navOverlayTransitions = useTransition(overlayVisible, {
    from: {
      transform: 'translateX(80%)',
    },
    enter: {
      transform: 'translateX(0%)',
      delay: 100,
    },
    leave: {
      transform: 'translateX(100%)',
      delay: 300,
    },
  });

  const currentTab = activeTab !== undefined ? tabs[activeTab] : undefined;

  return (
    <Wrapper>
      {tabs.map(({ label, badgeContent }, index) => (
        <TabWrapper key={index} hasBorderBottom={index !== tabs.length - 1} onClick={() => showOverlay(index)}>
          <TitleWrapper>
            <Heading as="span" size={5}>
              {label}
            </Heading>
            {badgeContent && (
              <BadgeContainer>
                <span>{badgeContent}</span>
              </BadgeContainer>
            )}
          </TitleWrapper>
          <ArrowContainer>
            <ChevronRightIcon className="w-[18px] h-[18px] text-primary-900" />
          </ArrowContainer>
        </TabWrapper>
      ))}
      {currentTab &&
        navOverlayTransitions((style, item) => {
          return item ? (
            <StyledNavOverlay hasScrolled={hasCrossedNavbar} style={style}>
              <OverlayTitleWrapper>
                <Button
                  variant="tertiary"
                  className="!p-0 !shadow-none focus:ring-0 hover:cursor-pointer"
                  onClick={hideOverlay}
                  StartIcon={ArrowLeftIcon}
                />
                <Heading as="span" size={5} weight="semibold">
                  {currentTab.title}
                </Heading>
              </OverlayTitleWrapper>
              <OverlayBody>{currentTab.content}</OverlayBody>
            </StyledNavOverlay>
          ) : null;
        })}
    </Wrapper>
  );
};

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('lg:hidden flex flex-col w-full items-start gap-3', className)} {...props} />
);

const TabWrapper = ({ hasBorderBottom, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-row items-center justify-between bg-white pb-3 w-full focus:cursor-pointer hover:cursor-pointer',
      hasBorderBottom ? 'border-b border-gray-300' : '',
      className
    )}
    {...props}
  />
);

const ArrowContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-row w-[34px] h-[34px] min-w-[34px] items-center justify-center bg-primary-50 rounded-full',
      className
    )}
    {...props}
  />
);

const StyledNavOverlay = ({
  hasScrolled,
  className,
  ...props
}: { hasScrolled: boolean } & React.ComponentProps<typeof animated.div>) => (
  <animated.div
    className={cn(
      'fixed box-border inset-0 top-[88px] z-20 flex flex-col justify-between bg-white rounded-tl-6.25 rounded-tr-6.25 h-[calc(100vh-88px)] max-h-[calc(100vh-88px)] max-w-[100vw] overflow-hidden',
      className
    )}
    {...props}
  />
);

const OverlayTitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'sticky z-10 top-0 bg-white flex flex-row items-center gap-[110px] h-20 min-h-[80px] w-full px-6',
      className
    )}
    {...props}
  />
);

const OverlayBody = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full h-full overflow-y-scroll max-h-full px-6', className)} {...props} />
);

const TitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-row items-center justify-start gap-2.5 w-fit', className)} {...props} />
);

export const BadgeContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center w-5 h-[18px] min-w-[20px] min-h-[18px] rounded-full text-xs font-semibold text-white bg-primary-700',
      className
    )}
    {...props}
  />
);
